/**
 * pages/Messages.tsx - Buyer-Seller Chat Page
 * Conversation list (sidebar) + message thread view
 * Uses /api/messages/conversations/* endpoints
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Conversation, Message, ChatRoom } from '@/types/message';
import { apiClient } from '@/lib/api-client';
import SEO from '@/components/common/SEO';
import { getAvatarUrl } from '@/lib/utils';
import { FeaturedRooms } from '@/components/chat/FeaturedRooms';
import { chatRoomService } from '@/lib/api/chat-rooms';
import {
  MessageSquare,
  Send,
  ArrowLeft,
  Loader2,
  AlertCircle,
  User,
  RefreshCw,
  ServerCrash,
  Sparkles,
  Bot,
  CheckCircle2,
  Compass,
} from 'lucide-react';

const ChatAvatar: React.FC<{
  avatar?: string | null;
  name?: string | null;
  className?: string;
  iconClassName?: string;
  isSystem?: boolean;
  isRoom?: boolean;
  roomIcon?: string;
}> = ({ avatar, name, className = 'h-10 w-10', iconClassName = 'h-5 w-5', isSystem, isRoom, roomIcon }) => {
  const [imgError, setImgError] = useState(false);
  const avatarSrc = getAvatarUrl(avatar);

  if (isRoom) {
    return (
      <div className={`flex ${className} shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary font-bold text-base border border-primary/20 shadow-xs`}>
        <span>{roomIcon || '💬'}</span>
      </div>
    );
  }

  if (isSystem || name?.includes('Assistant') || name?.includes('Bot') || name?.includes('System')) {
    return (
      <div className={`flex ${className} shrink-0 items-center justify-center rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold text-sm border border-blue-500/20`}>
        <Bot className={iconClassName} />
      </div>
    );
  }

  if (avatarSrc && !imgError) {
    return (
      <img
        src={avatarSrc}
        alt={name || 'User'}
        onError={() => setImgError(true)}
        className={`${className} shrink-0 rounded-full object-cover border border-border/40`}
      />
    );
  }

  const initial = name?.trim() ? name.trim()[0].toUpperCase() : null;

  return (
    <div className={`flex ${className} shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-sm border border-primary/20`}>
      {initial ? <span>{initial}</span> : <User className={iconClassName} />}
    </div>
  );
};

const MessagesPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);      // Other party typing
  const [iAmTyping, setIAmTyping] = useState(false);     // Current user typing
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
  const [chatTab, setChatTab] = useState<'ALL' | 'DIRECT' | 'ROOMS' | 'UNREAD'>('ALL');
  const [featuredRooms, setFeaturedRooms] = useState<ChatRoom[]>([]);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingEventSourceRef = useRef<EventSource | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const roomJoinedFromUrlRef = useRef<string | null>(null);
  const loadingRoomMessagesRef = useRef<string | null>(null);
  const conversationsRef = useRef<Conversation[]>([]);

  const joinRoom = (room: ChatRoom, updateUrl = true) => {
    // Guard: don't re-join if already viewing this room
    if (selectedConversation?.id === room.id && selectedConversation?.isRoom) {
      return;
    }

    const roomConv: Conversation = {
      id: room.id,
      participantId: room.id,
      participantName: room.name,
      participantAvatar: undefined,
      lastMessage: room.topic || room.description,
      lastMessageTime: new Date().toISOString(),
      unreadCount: 0,
      isRoom: true,
      isSystem: room.isSystem,
      topic: room.topic,
      onlineCount: room.onlineCount,
      badge: room.badge,
    };

    setConversations(prev => {
      if (prev.some(c => c.id === room.id)) return prev;
      const updated = [roomConv, ...prev];
      conversationsRef.current = updated;
      return updated;
    });

    setSelectedConversation(roomConv);
    loadRoomMessages(room.id);
    if (updateUrl) {
      navigate(`/messages?roomId=${room.slug}`, { replace: true });
    }
  };

  const loadRoomMessages = async (roomId: string) => {
    // Deduplicate: don't load if already loading this room
    if (loadingRoomMessagesRef.current === roomId) return;
    loadingRoomMessagesRef.current = roomId;

    setLoading(true);
    try {
      // Resolve slug from featuredRooms or use roomId as slug
      const room = featuredRooms.find(r => r.id === roomId || r.slug === roomId);
      const slug = room?.slug || roomId;
      const raw = await chatRoomService.getRoomMessages(slug);
      const mapped: Message[] = raw.map(m => ({
        id: m.id,
        conversationId: roomId,
        senderId: m.senderId,
        senderName: m.senderName,
        senderAvatar: m.senderAvatar,
        text: m.text,
        createdAt: m.createdAt,
        isRead: true,
      }));
      setMessages(mapped);
    } catch (err) {
      console.error('Error loading room messages:', err);
    } finally {
      setLoading(false);
      loadingRoomMessagesRef.current = null;
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    loadConversations();
  }, [isAuthenticated, navigate]);

  // Fetch featured rooms from backend API
  useEffect(() => {
    chatRoomService.getFeaturedRooms().then(setFeaturedRooms);
  }, []);

  // Heartbeat: ping the server every 2 minutes while user is in a room
  useEffect(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (!selectedConversation?.isRoom) return;

    const room = featuredRooms.find(
      r => r.id === selectedConversation.id || r.slug === selectedConversation.id
    );
    const slug = room?.slug || selectedConversation.id;

    // Send initial heartbeat immediately
    chatRoomService.sendHeartbeat(slug).then(count => {
      if (count !== null) {
        setFeaturedRooms(prev => prev.map(r => r.slug === slug ? { ...r, onlineCount: count } : r));
      }
    });

    // Then every 2 minutes
    heartbeatIntervalRef.current = setInterval(() => {
      chatRoomService.sendHeartbeat(slug).then(count => {
        if (count !== null) {
          setFeaturedRooms(prev => prev.map(r => r.slug === slug ? { ...r, onlineCount: count } : r));
        }
      });
    }, 120_000);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [selectedConversation?.isRoom, selectedConversation?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (typingEventSourceRef.current) typingEventSourceRef.current.close();
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
    };
  }, []);

  const initiatingRef = useRef<string | null>(null);

  // Auto-select conversation or room from URL params
  // IMPORTANT: `conversations` removed from deps to prevent infinite loop.
  // Uses conversationsRef.current for lookups instead.
  useEffect(() => {
    let timer: NodeJS.Timeout;
    const handleUrlParams = async () => {
      const roomIdParam = searchParams.get('roomId');
      if (roomIdParam) {
        // Guard: already joined this room from URL
        if (roomJoinedFromUrlRef.current === roomIdParam) return;

        const foundRoom = featuredRooms.find(r => r.slug === roomIdParam || r.id === roomIdParam);
        if (foundRoom) {
          roomJoinedFromUrlRef.current = roomIdParam;
          joinRoom(foundRoom, false);
          return;
        }
      }

      const conversationId = searchParams.get('conversationId');
      const listingId = searchParams.get('listingId');
      const friendUserId = searchParams.get('userId');
      const friendNameParam = searchParams.get('name');
      const friendAvatarParam = searchParams.get('avatar');
      
      if (conversationId) {
        const conv = conversationsRef.current.find(c => c.id === conversationId);
        if (conv) {
          setSelectedConversation(conv);
        } else if (!loading) {
          try {
            const data = await apiClient.get(`/api/messages/conversations/${conversationId}`);
            if (data && data.id) {
              const mappedConv: Conversation = {
                id: data.id,
                participantId: data.participantId || '',
                participantName: data.participantName || 'User',
                participantAvatar: data.participantAvatar,
                lastMessage: data.lastMessage || '',
                lastMessageTime: data.lastMessageTime || new Date().toISOString(),
                unreadCount: 0,
                listingId: data.listingId,
                listingTitle: data.listingTitle,
              };
              setConversations(prev => {
                const updated = [mappedConv, ...prev.filter(c => c.id !== mappedConv.id)];
                conversationsRef.current = updated;
                return updated;
              });
              setSelectedConversation(mappedConv);
            }
          } catch (e) {
            console.error('Failed to load conversation by ID', e);
          }
        }
      } else if (listingId) {
        const conv = conversationsRef.current.find(c => c.listingId === listingId);
        if (conv) setSelectedConversation(conv);
      } else if (friendUserId && initiatingRef.current !== friendUserId) {
        let conv = conversationsRef.current.find(c => c.participantId === friendUserId && !c.listingId);
        if (conv) {
          setSelectedConversation(conv);
        } else {
          initiatingRef.current = friendUserId;
          const friendName = friendNameParam || 'Friend';

          const optimisticConv: Conversation = {
            id: `temp-${friendUserId}`,
            participantId: friendUserId,
            participantName: friendName,
            participantAvatar: friendAvatarParam || undefined,
            lastMessage: 'Hi 👋',
            lastMessageTime: new Date().toISOString(),
            unreadCount: 0,
          };
          setSelectedConversation(prev => (prev && prev.participantId === friendUserId ? prev : optimisticConv));

          try {
            const newConv = await apiClient.post('/api/messages/conversations', {
              recipientId: friendUserId,
              initialMessage: 'Hi 👋'
            });

            if (newConv && newConv.id) {
              const mappedConv: Conversation = {
                id: newConv.id,
                participantId: newConv.participantId || friendUserId,
                participantName: newConv.participantName || friendName,
                participantAvatar: newConv.participantAvatar || friendAvatarParam || undefined,
                lastMessage: newConv.lastMessage || 'Hi 👋',
                lastMessageTime: newConv.lastMessageTime || new Date().toISOString(),
                unreadCount: 0,
              };
              setConversations(prev => {
                const updated = [mappedConv, ...prev.filter(c => c.id !== mappedConv.id && c.id !== optimisticConv.id)];
                conversationsRef.current = updated;
                return updated;
              });
              setSelectedConversation(mappedConv);
              setError(null);

              navigate(`/messages?conversationId=${newConv.id}`, { replace: true });
            }
          } catch (err: any) {
            console.error('Failed to initiate friend chat:', err);
            const msg = err?.message?.includes('503') 
              ? 'Messaging service is connecting, retrying...' 
              : (err?.message || 'Failed to start conversation');
            setError(msg);
            if (err?.message?.includes('503')) {
              timer = setTimeout(() => {
                initiatingRef.current = null;
                handleUrlParams();
              }, 4000);
            }
          } finally {
            if (!timer) initiatingRef.current = null;
          }
        }
      }
    };
    
    handleUrlParams();
    return () => { if (timer) clearTimeout(timer); };
  }, [searchParams, loading, featuredRooms, navigate]);

  // Load messages when conversation changes (by ID, not object reference)
  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation?.id]);

  // SSE: listen for typing events from the other party
  useEffect(() => {
    if (!selectedConversation || selectedConversation.isRoom) return;
    // Close previous SSE connection
    if (typingEventSourceRef.current) {
      typingEventSourceRef.current.close();
    }
    const token = localStorage.getItem('accessToken');
    const es = new EventSource(
      `/api/messages/conversations/${selectedConversation.id}/typing${token ? `?access_token=${token}` : ''}`
    );
    typingEventSourceRef.current = es;
    es.onmessage = (event) => {
      if (event.data === 'typing_start') setIsTyping(true);
      if (event.data === 'typing_stop') setIsTyping(false);
    };
    es.onerror = () => { setIsTyping(false); };
    return () => { es.close(); };
  }, [selectedConversation?.id, selectedConversation?.isRoom]);

  // Send typing broadcast via POST when user types
  const handleTypingBroadcast = () => {
    if (!selectedConversation || selectedConversation.isRoom || iAmTyping) return;
    setIAmTyping(true);
    apiClient.post(`/api/messages/conversations/${selectedConversation.id}/typing`, { typing: true })
      .catch(() => {});
    // Clear previous timer
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      setIAmTyping(false);
      apiClient.post(`/api/messages/conversations/${selectedConversation.id}/typing`, { typing: false })
        .catch(() => {});
    }, 3000);
  };

  // Auto-scroll to bottom inside the messages container ONLY
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  // Jump to bottom immediately when switching conversation
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'auto',
      });
    }
  }, [selectedConversation?.id]);

  const loadConversations = async () => {
    setLoading(true);
    setError(null);
    setConnectionStatus('connecting');
    try {
      const data = await apiClient.get('/api/messages/conversations?size=50');
      // Backend returns Spring Data Page format with 'content' array
      const convList: Conversation[] = data?.content ?? data?.conversations ?? (Array.isArray(data) ? data : []);

      // Resolve participant names and avatars from user-service
      const uniqueIds = [...new Set(convList.map(c => c.participantId).filter(Boolean))];
      const userMap: Record<string, { name?: string; avatar?: string }> = {};
      await Promise.all(
        uniqueIds.map(async (pid) => {
          try {
            const userData = await apiClient.get(`/api/users/public/${pid}`);
            if (userData) {
              userMap[pid] = {
                name: userData.name,
                avatar: userData.avatar || userData.avatarUrl,
              };
            }
          } catch { /* ignore */ }
        })
      );

      const enriched = convList.map(c => ({
        ...c,
        participantName: c.participantName || userMap[c.participantId]?.name || 'User',
        participantAvatar: c.participantAvatar || userMap[c.participantId]?.avatar || undefined,
      }));

      setConversations(enriched);
      conversationsRef.current = enriched;
      setConnectionStatus('connected');
    } catch (err: any) {
      console.error('Error loading conversations:', err);
      
      // Detailed error handling
      if (err?.status === 429) {
        setError('Too many requests, please wait a moment...');
        setConnectionStatus('error');
        // Back off longer for rate limiting
        retryTimeoutRef.current = setTimeout(() => {
          loadConversations();
        }, 30000);
      } else if (err?.status === 500) {
        setError('Server internal error, please try again later');
        setConnectionStatus('error');
        // Auto-retry after 10 seconds for 500 errors
        retryTimeoutRef.current = setTimeout(() => {
          loadConversations();
        }, 10000);
      } else if (err?.status === 401) {
        setError('Login expired, please log in again');
        navigate('/login');
      } else if (err?.status === 503) {
        setError('Messaging service is temporarily unavailable, retrying...');
        setConnectionStatus('error');
        // Retry after 5 seconds for 503 errors
        retryTimeoutRef.current = setTimeout(() => {
          loadConversations();
        }, 5000);
      } else if (err?.message?.includes('Failed to fetch') || err?.message?.includes('network')) {
        setError('Network connection failed, please check your internet and try again');
        setConnectionStatus('error');
        // Retry after 5 seconds for network errors
        retryTimeoutRef.current = setTimeout(() => {
          loadConversations();
        }, 5000);
      } else {
        setError(err?.message || 'Failed to load conversations');
        setConnectionStatus('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (convId: string) => {
    if (convId.startsWith('room-') || selectedConversation?.isRoom) {
      loadRoomMessages(convId);
      return;
    }

    setLoading(true);
    try {
      const data = await apiClient.get(`/api/messages/conversations/${convId}/messages`);
      // Backend returns List<MessageResponse> directly (a plain array)
      // Map backend field names to frontend Message type
      const msgList = Array.isArray(data) ? data : (data?.messages ?? data?.content ?? []);
      const mappedMessages: Message[] = msgList.map((msg: any) => ({
        id: msg.id,
        conversationId: msg.inquiryId || msg.conversationId || convId,
        senderId: msg.senderId,
        senderName: msg.senderName || msg.senderType || '',
        senderAvatar: msg.senderAvatar,
        text: msg.content || msg.text || '',
        createdAt: msg.createdAt,
        isRead: msg.isRead ?? msg.read ?? false,
      }));
      setMessages(mappedMessages);
    } catch (err: any) {
      console.error('Error loading messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = async (action: string, url?: string) => {
    if (url) {
      navigate(url);
      return;
    }
    if (action === 'hello' || action === 'greet') {
      if (selectedConversation?.isRoom) {
        const greeting = 'Hello everyone! 👋 Glad to join MemAds.';
        const room = featuredRooms.find(r => r.id === selectedConversation.id || r.slug === selectedConversation.id);
        const slug = room?.slug || selectedConversation.id;
        const userMsg = await chatRoomService.sendRoomMessage(
          slug,
          {
            id: user?.sub || 'user-me',
            name: user?.name || 'Me',
            avatar: user?.avatar,
          },
          greeting
        );
        if (userMsg) {
          setMessages(prev => [...prev, {
            id: userMsg.id,
            conversationId: selectedConversation.id,
            senderId: userMsg.senderId,
            senderName: userMsg.senderName,
            text: userMsg.text,
            createdAt: userMsg.createdAt,
            isRead: true,
          }]);
        }
      }
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    if (selectedConversation.isRoom) {
      const room = featuredRooms.find(r => r.id === selectedConversation.id || r.slug === selectedConversation.id);
      const slug = room?.slug || selectedConversation.id;
      const msgText = newMessage.trim();
      setNewMessage('');
      setSending(true);

      try {
        const userMsg = await chatRoomService.sendRoomMessage(
          slug,
          {
            id: user?.sub || 'user-me',
            name: user?.name || 'Me',
            avatar: user?.avatar,
          },
          msgText
        );
        if (userMsg) {
          const sent: Message = {
            id: userMsg.id,
            conversationId: selectedConversation.id,
            senderId: userMsg.senderId,
            senderName: userMsg.senderName,
            senderAvatar: userMsg.senderAvatar,
            text: userMsg.text,
            createdAt: userMsg.createdAt,
            isRead: true,
          };
          setMessages(prev => [...prev, sent]);
        }
      } catch (err) {
        console.error('Error sending room message:', err);
        setNewMessage(msgText); // Restore message on failure
      } finally {
        setSending(false);
      }
      return;
    }

    setSending(true);
    try {
      let targetConvId = selectedConversation.id;

      if (targetConvId.startsWith('temp-')) {
        const createdConv = await apiClient.post('/api/messages/conversations', {
          recipientId: selectedConversation.participantId,
          initialMessage: newMessage.trim(),
        });
        if (createdConv && createdConv.id) {
          targetConvId = createdConv.id;
          const mappedConv: Conversation = {
            ...selectedConversation,
            id: createdConv.id,
            lastMessage: newMessage.trim(),
            lastMessageTime: new Date().toISOString(),
          };
          setSelectedConversation(mappedConv);
          setConversations(prev => {
            const updated = [mappedConv, ...prev.filter(c => c.id !== mappedConv.id && c.id !== selectedConversation.id)];
            conversationsRef.current = updated;
            return updated;
          });
          const sent: Message = {
            id: `msg-${Date.now()}`,
            conversationId: createdConv.id,
            senderId: user?.sub || '',
            senderName: user?.name || 'Me',
            text: newMessage.trim(),
            createdAt: new Date().toISOString(),
            isRead: false,
          };
          setMessages(prev => [...prev, sent]);
          setNewMessage('');
          navigate(`/messages?conversationId=${createdConv.id}`, { replace: true });
          return;
        }
      }

      const sentData = await apiClient.post(
        `/api/messages/conversations/${targetConvId}/messages`,
        { content: newMessage.trim() }
      );
      // Map backend response to frontend Message type
      const sent: Message = {
        id: sentData.id || `msg-${Date.now()}`,
        conversationId: sentData.inquiryId || sentData.conversationId || targetConvId,
        senderId: sentData.senderId || user?.sub || '',
        senderName: sentData.senderName || sentData.senderType || '',
        text: sentData.content || sentData.text || newMessage.trim(),
        createdAt: sentData.createdAt || new Date().toISOString(),
        isRead: false,
      };
      setMessages(prev => [...prev, sent]);
      setNewMessage('');
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-IN');
  };

  const handleRetry = () => {
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    loadConversations();
  };

  if (connectionStatus === 'error' && !loading && conversations.length === 0 && error) {
    return (
      <div className="min-h-screen bg-background flex">
        <SEO title="Messages" noindex />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <ServerCrash className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Service Temporarily Unavailable</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition"
              >
                Go Home
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              If this issue persists, please contact support@memads.in
            </p>
          </div>
        </div>
      </div>
    );
  }

  const filteredConversations = conversations.filter(conv => {
    if (chatTab === 'DIRECT') return !conv.isRoom;
    if (chatTab === 'ROOMS') return conv.isRoom;
    if (chatTab === 'UNREAD') return conv.unreadCount > 0;
    return true;
  });

  return (
    <div className="w-full bg-background flex overflow-hidden" style={{ flex: '1 1 0%', minHeight: 0 }}>
      <SEO title="Messages & Chat Rooms" noindex />
      {/* Conversation List Sidebar */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-border h-full flex flex-col min-h-0 shrink-0 ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
          <h1 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-primary" />
            Messages
            {connectionStatus === 'connecting' && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
            {connectionStatus === 'connected' && (
              <span className="h-2 w-2 rounded-full bg-green-500" />
            )}
            {connectionStatus === 'error' && (
              <span className="h-2 w-2 rounded-full bg-destructive" />
            )}
          </h1>

          <button
            type="button"
            onClick={() => {
              setSelectedConversation(null);
              setChatTab('ROOMS');
            }}
            title="Explore Featured Chat Rooms"
            className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          >
            <Compass className="h-3.5 w-3.5" />
            <span>Rooms</span>
          </button>
        </div>

        {/* Tab Filters (y99.in style: ALL | PEOPLE | ROOMS | UNREAD) */}
        <div className="flex border-b border-border/60 bg-muted/20 px-3 py-2 gap-1.5 overflow-x-auto text-xs font-semibold shrink-0">
          {[
            { key: 'ALL', label: 'ALL' },
            { key: 'DIRECT', label: 'PEOPLE' },
            { key: 'ROOMS', label: 'ROOMS' },
            { key: 'UNREAD', label: 'UNREAD' },
          ].map((tab) => {
            const isActive = chatTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setChatTab(tab.key as any);
                  if (tab.key === 'ROOMS' && selectedConversation && !selectedConversation.isRoom) {
                    setSelectedConversation(null);
                  }
                }}
                className={`px-3 py-1 rounded-full whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-xs'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/70'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {loading && conversations.length === 0 && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {error && conversations.length === 0 && connectionStatus !== 'error' && (
          <div className="p-4 shrink-0">
            <div className="rounded-lg bg-destructive/5 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{error}</span>
              <button
                onClick={handleRetry}
                className="text-xs font-semibold px-2 py-1 rounded bg-destructive/10 text-destructive hover:bg-destructive/20 shrink-0 transition"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {chatTab === 'ROOMS' ? (
          /* Public Chat Rooms List (y99.in style) */
          <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-border/40">
            <div className="p-3 bg-muted/40 text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
              <span>Public Chat Rooms</span>
              <span className="text-primary font-normal">Active</span>
            </div>
            {featuredRooms.map(room => (
              <button
                key={room.id}
                onClick={() => joinRoom(room)}
                className={`w-full p-3.5 flex gap-3 hover:bg-muted/50 transition text-left border-b border-border ${
                  selectedConversation?.id === room.id ? 'bg-muted' : ''
                }`}
              >
                <ChatAvatar
                  className="h-10 w-10"
                  isRoom
                  roomIcon={room.icon}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-semibold text-foreground truncate text-sm">
                      {room.name}
                    </span>
                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold shrink-0">
                      🟢 {room.onlineCount}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {room.topic || room.description}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          /* Filtered Conversation List */
          <div className="flex-1 overflow-y-auto min-h-0">
            {!loading && filteredConversations.length === 0 && connectionStatus !== 'error' && (
              <div className="p-6 text-center space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto">
                  <MessageSquare className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground text-sm">
                    {chatTab === 'UNREAD' ? 'No unread messages' : 'No private chats yet'}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto">
                    {chatTab === 'UNREAD' 
                      ? 'You are all caught up!'
                      : 'When you contact sellers or receive inquiries, they will appear here.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const welcome = featuredRooms[0];
                    if (welcome) joinRoom(welcome);
                  }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition shadow-xs"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Join Welcome Room</span>
                </button>
              </div>
            )}

            {filteredConversations.map(conv => (
              <button
                key={conv.id}
                onClick={() => {
                  setSelectedConversation(conv);
                  if (conv.isRoom) {
                    loadRoomMessages(conv.id);
                  }
                }}
                className={`w-full p-4 flex gap-3 hover:bg-muted/50 transition text-left border-b border-border ${
                  selectedConversation?.id === conv.id ? 'bg-muted' : ''
                }`}
              >
                <ChatAvatar
                  avatar={conv.participantAvatar}
                  name={conv.participantName}
                  className="h-10 w-10"
                  iconClassName="h-5 w-5"
                  isRoom={conv.isRoom}
                  isSystem={conv.isSystem}
                  roomIcon={conv.isRoom ? '💬' : undefined}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-foreground truncate">
                      {conv.participantName || 'Unknown Seller'}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {conv.isRoom ? `🟢 ${conv.onlineCount || 0}` : (conv.lastMessageTime ? formatTime(conv.lastMessageTime) : '')}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {conv.isRoom ? (conv.badge || 'Community Room') : (conv.listingTitle || 'Listing')}
                  </p>
                  <p className="text-sm text-muted-foreground truncate mt-1">
                    {conv.lastMessage}
                  </p>
                </div>
                {conv.unreadCount > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-primary-foreground shrink-0">
                    {conv.unreadCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Message Thread or Featured Rooms */}
      <div className={`flex-1 h-full flex flex-col min-h-0 overflow-hidden ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        {selectedConversation ? (
          <>
            {/* Thread Header */}
            <div className="flex items-center gap-3 p-4 border-b border-border bg-card shrink-0">
              <button
                onClick={() => setSelectedConversation(null)}
                className="md:hidden flex h-8 w-8 items-center justify-center rounded-lg hover:bg-secondary transition"
              >
                <ArrowLeft className="h-4 w-4 text-foreground" />
              </button>
              <ChatAvatar
                avatar={selectedConversation.participantAvatar}
                name={selectedConversation.participantName}
                className="h-10 w-10"
                iconClassName="h-5 w-5"
                isRoom={selectedConversation.isRoom}
                isSystem={selectedConversation.isSystem}
                roomIcon={selectedConversation.isRoom ? '💬' : undefined}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-foreground truncate">
                    {selectedConversation.participantName || 'Chat'}
                  </p>
                  {selectedConversation.isRoom && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                      <CheckCircle2 className="h-3 w-3 fill-blue-500/20" />
                      Verified
                    </span>
                  )}
                </div>
                {selectedConversation.isRoom ? (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {selectedConversation.onlineCount || 0} members online
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground truncate">
                    {selectedConversation.listingTitle || 'Direct Conversation'}
                  </p>
                )}
              </div>
            </div>

            {/* Pinned Topic Banner for Rooms (y99.in signature feature) */}
            {selectedConversation.isRoom && selectedConversation.topic && (
              <div className="bg-muted/70 dark:bg-muted/30 border-b border-border px-4 py-2.5 text-xs text-muted-foreground flex items-center gap-2 shrink-0">
                <span className="font-bold text-foreground shrink-0">📌 Topic:</span>
                <span className="truncate">{selectedConversation.topic}</span>
              </div>
            )}

            {/* Messages Stream */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto min-h-0 p-4 space-y-3">
              {messages.map((msg) => {
                const isMine = msg.senderId === user?.sub;
                const isBot = msg.senderId === 'system-bot';

                return (
                  <div
                    key={msg.id}
                    className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                  >
                    {!isMine && (
                      <div className="mr-2 self-end mb-1">
                        <ChatAvatar
                          avatar={msg.senderAvatar || selectedConversation.participantAvatar}
                          name={msg.senderName || selectedConversation.participantName}
                          className="h-7 w-7"
                          iconClassName="h-3.5 w-3.5"
                          isSystem={isBot}
                        />
                      </div>
                    )}
                    <div
                      className={`max-w-[85%] md:max-w-[75%] rounded-2xl px-4 py-2.5 ${
                        isMine
                          ? 'bg-primary text-primary-foreground rounded-br-md'
                          : isBot
                          ? 'bg-card border border-primary/30 text-foreground rounded-bl-md shadow-xs'
                          : 'bg-muted text-foreground rounded-bl-md'
                      }`}
                    >
                      {!isMine && (
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-xs font-semibold text-primary">
                            {msg.senderName}
                          </span>
                          {isBot && (
                            <span className="text-[10px] bg-primary/15 text-primary px-1.5 py-0.2 rounded font-bold">
                              BOT
                            </span>
                          )}
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>

                      {/* Quick action chips on bot welcome messages */}
                      {msg.quickActions && msg.quickActions.length > 0 && (
                        <div className="mt-3 pt-2.5 border-t border-border/50 flex flex-wrap gap-1.5">
                          {msg.quickActions.map((qa, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleQuickAction(qa.action, qa.url)}
                              className="text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-all active:scale-95 flex items-center gap-1"
                            >
                              <span>{qa.label}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      <p className={`text-[10px] mt-1.5 ${isMine ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                        {formatTime(msg.createdAt)}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Typing Indicator */}
            {isTyping && (
              <div className="px-4 py-1 text-xs text-muted-foreground italic shrink-0">
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  <span className="ml-1">typing...</span>
                </span>
              </div>
            )}

            {/* Reply Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-border bg-card shrink-0">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={e => { setNewMessage(e.target.value); handleTypingBroadcast(); }}
                  placeholder={selectedConversation.isRoom ? "Send a message to Welcome Room..." : "Type a message..."}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground transition hover:brightness-110 disabled:opacity-50 shrink-0 shadow-xs"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <FeaturedRooms rooms={featuredRooms} onJoinRoom={joinRoom} />
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
